import axios from 'axios';
import { ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { triggerGlobalToastByKey } from '@/composables/useToast';

export const apiVersion = ref('Loading...');

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';
axios.defaults.baseURL = import.meta.env.VITE_API_URL;
// Toleransi jaringan lambat untuk pengguna di daerah (misal: 3G/4G tidak stabil)
axios.defaults.timeout = 60000;

let csrfPromise = null;

/**
 * Mendapatkan CSRF cookie dari Laravel Sanctum
 */
export function csrf() {
  if (csrfPromise) return csrfPromise;

  csrfPromise = axios
    .get('sanctum/csrf-cookie')
    .then(() => {
      return true;
    })
    .catch((error) => {
      csrfPromise = null;
      throw error;
    });

  return csrfPromise;
}

// Interceptor untuk memastikan CSRF token diambil sebelum request non-GET
axios.interceptors.request.use(async (config) => {
  // Hanya ambil CSRF untuk request yang membutuhkan (POST, PUT, DELETE, PATCH)
  // dan pastikan tidak loop pada request csrf-cookie itu sendiri
  const needsCsrf = ['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase());
  const isCsrfRequest = config.url?.includes('csrf-cookie');

  if (needsCsrf && !isCsrfRequest) {
    try {
      await csrf();

      // Manual injection if automatic fails
      const cookies = document.cookie.split('; ');
      const token = cookies.find((row) => row.startsWith('XSRF-TOKEN='))?.split('=')[1];
      if (token) {
        config.headers['X-XSRF-TOKEN'] = decodeURIComponent(token);
      }
    } catch {
      // Biarkan request berlanjut
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    const versionHeader = response.headers['x-app-version'] || response.headers['X-App-Version'];
    if (versionHeader) {
      apiVersion.value = versionHeader;
    } else if (response.data && response.data.version) {
      apiVersion.value = response.data.version;
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 419) {
      csrfPromise = null;
      const originalRequest = error.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        try {
          await csrf();
          return axios(originalRequest);
        } catch {
          // retry gagal, lanjutkan error
        }
      }
    }

    if (error.response?.status === 401) {
      const authStore = useAuthStore();
      if (authStore.isAuthenticated) {
        authStore.user = null;
        window.location.href = '/login';
      }
    }

    // Global error toast notifications (i18n keys, translated via AppToast)
    if (error.response) {
      const status = error.response.status;

      if (status === 403) {
        triggerGlobalToastByKey('cv.error.forbidden');
      } else if (status === 429) {
        triggerGlobalToastByKey('cv.error.tooManyRequests');
      } else if (status === 500) {
        triggerGlobalToastByKey('cv.error.serverError');
      }
    }

    return Promise.reject(error);
  }
);
